import { Link } from 'react-router';
import { useMapRecord, useMaps } from '../api/queries';
import { useT } from '../i18n';
import { useLive } from '../live/store';
import { fmt } from '../lib/format';
import { stuckText, useStuck } from '../lib/stuck';

/** §20 banner over the robot camera: amber while recovering, red after a final failure. */
export function StuckBanner({ serial }: { serial: string }) {
  const t = useT();
  const v = useStuck(serial);
  const st = useLive((s) => s.robots[serial]?.status);
  const dismiss = useLive((s) => s.setNavStuck);
  const maps = useMaps();
  const mapId = maps.data?.find((m) => m.name === st?.currentMap)?.id;
  const map = useMapRecord(v?.kind === 'failed' ? mapId : undefined);
  if (!v) return null;
  if (v.kind === 'active')
    return (
      <div className="stuck-banner" role="status" aria-live="polite">
        <span className="stuck-pulse" aria-hidden />
        <span>⚠ {stuckText(v)}</span>
        {v.reason && <span className="mono stuck-reason">{v.reason}</span>}
      </div>
    );
  const loc = v.location ?? st?.location;
  const goal =
    typeof v.goal === 'string' || v.pointId
      ? (map.data?.points.find((p) => p.id === (v.pointId ?? v.goal))?.name ?? String(v.pointId ?? v.goal))
      : v.goal
        ? `${fmt(v.goal.x, 1)}, ${fmt(v.goal.y, 1)}`
        : null;
  return (
    <div className="stuck-banner failed" role="alert">
      <div className="col" style={{ gap: 2, minWidth: 0 }}>
        <b>⛔ {t('stuck.failed')}</b>
        <span className="stuck-sub">
          {loc && t('stuck.failedAt', { x: fmt(loc.x, 2), y: fmt(loc.y, 2) })}
          {goal && ` · ${t('stuck.goal', { goal })}`}
          {v.reason && ` · ${v.reason}`}
        </span>
      </div>
      <span className="grow" />
      {mapId && (
        <Link className="btn xs" to={`/maps/${mapId}?follow=${encodeURIComponent(serial)}`}>
          {t('stuck.showOnMap')}
        </Link>
      )}
      <button type="button" className="btn xs ghost" onClick={() => dismiss(serial, null)} aria-label={t('stuck.dismiss')} title={t('stuck.dismiss')}>
        ×
      </button>
    </div>
  );
}
